import { objectEntries, type ReadonlyRecord } from '../../../utils'

import {
    type AppVersions,
    Capability,
    ConnectwareError,
    ConnectwareErrorType,
    type ConnectwareLicense,
    type EndpointConnectivity,
    type Metrics,
    type SystemConnectivity,
} from '../../../domain'

import type { SystemService } from '../../../application'

import type { BackendJsonResponseContent } from '../../Connectware'
import { type ConnectwareHTTPServiceOptions, FetchConnectwareHTTPService, type HttpRequestArgs } from '../Base'
import { isVersionOutdated, mapLicenseRequestError } from './Mappers'

type Config = Pick<HttpRequestArgs<'/api/system/preflight', 'GET', never>, 'capability' | 'method' | 'path'>

export class CybusSystemService extends FetchConnectwareHTTPService implements SystemService {
    private readonly systemReadConfig: Config = { capability: Capability.MINIMUM, method: 'GET', path: '/api/system/preflight' }
    private readonly metricsReadConfig: Config = { capability: Capability.SYSTEM_METRICS_READ, method: 'GET', path: '/api/system/preflight' }

    constructor (options: ConnectwareHTTPServiceOptions, private readonly uiVersion: AppVersions['ui']) {
        super(options)
    }

    private fetchSystemInfo<T> (mapper: (response: BackendJsonResponseContent<'/api/system/info', 'GET', 200>) => T): Promise<T> {
        return this.request<'/api/system/info', 'GET', T>({
            capability: Capability.MINIMUM,
            method: 'GET',
            path: '/api/system/info',
            authenticate: true,
            handlers: { 200: (response) => response.getJson().then(mapper) },
        })
    }

    private fetchInternalConnectivity (config: 'systemReadConfig' | 'metricsReadConfig'): Promise<SystemConnectivity> {
        return this.request<'/api/system/preflight', 'GET', SystemConnectivity>({
            ...this[config],
            authenticate: true,
            handlers: {
                200: (response) =>
                    response.getJson().then(({ currentVersions, cybusPortal, cybusRegistry, dockerHub }) => {
                        const latest = currentVersions?.series?.active?.latest

                        const endpoints = { cybusPortal, cybusRegistry, dockerHub }
                        return {
                            endpoints: objectEntries(endpoints).reduce(
                                (r, [name, { status, url }]) => ({ ...r, [name]: { url: new URL(url), reachable: status } }),
                                {} as ReadonlyRecord<keyof typeof endpoints, EndpointConnectivity>
                            ),
                            latest: latest
                                ? { version: latest.version, releaseNotes: new URL(latest.urlReleaseNotes), installerURL: new URL(latest.installerUrl) }
                                : null,
                        }
                    }),
            },
        })
    }

    getUIVersion (): string {
        return this.uiVersion
    }

    async fetchConnectivity (): Promise<SystemConnectivity> {
        const connectivity = await this.fetchInternalConnectivity('systemReadConfig')

        if (!connectivity.latest?.version) {
            return connectivity
        }

        const currentVersion = this.getUIVersion()

        if (currentVersion === connectivity.latest.version) {
            return connectivity
        }

        const isCurrentVersionOutdated = isVersionOutdated(connectivity.latest.version, currentVersion)

        if (isCurrentVersionOutdated) {
            /**
             * Current connectware is out of date
             * And that is ok
             */
            return connectivity
        }

        /**
         * Current version is ahead, so just say the latest is the current
         */
        return { ...connectivity, latest: { ...connectivity.latest, version: currentVersion } }
    }

    fetchLicense (): Promise<ConnectwareLicense> {
        return this.fetchSystemInfo(({ license: { file: { id, associatedAccount, connectwareLicenseClass, expiration } = {}, name, valid } }) => {
            const parsedExpiration = (expiration && new Date(expiration)) ?? null

            if (!id || !associatedAccount || !name || !connectwareLicenseClass || !parsedExpiration || isNaN(parsedExpiration.getTime())) {
                return { id: null, activated: false, account: null, name: null, class: null, expiration: null, valid: false }
            }

            return {
                id,
                activated: true,
                account: associatedAccount,
                name,
                class: connectwareLicenseClass,
                expiration: parsedExpiration,
                valid: Boolean(valid),
            }
        })
    }

    refreshLicense (): Promise<void> {
        return this.request<'/api/system/refresh', 'GET', void>({
            capability: Capability.SYSTEM_LICENSE_MANAGE,
            method: 'GET',
            path: '/api/system/refresh',
            authenticate: true,
            handlers: { 200: () => Promise.resolve(), 400: (response) => mapLicenseRequestError(response, 'unableToRefresh') },
        })
    }

    uploadLicenseFile (payload: string): Promise<void> {
        return this.request<'/api/system/licensefile', 'PUT', void>({
            capability: Capability.SYSTEM_LICENSE_MANAGE,
            method: 'PUT',
            path: '/api/system/licensefile',
            authenticate: true,
            body: { payload },
            handlers: { 200: () => Promise.resolve(), 400: (response) => mapLicenseRequestError(response, 'unableToUpdate') },
        })
    }

    async fetchVersions (): Promise<AppVersions> {
        return { ui: this.getUIVersion(), connectware: await this.fetchSystemInfo((data) => data.version) }
    }

    fetchMetrics (): Promise<Metrics> {
        return Promise.all([
            this.request<'/api/system/metrics', 'GET', BackendJsonResponseContent<'/api/system/metrics', 'GET', 200>>({
                capability: Capability.SYSTEM_METRICS_READ,
                method: 'GET',
                path: '/api/system/metrics',
                authenticate: true,
                queryParams: { daily: 'true', hourly: 'true' },
                handlers: {
                    200: (response) => response.getJson(),
                    400: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'Unable to fetch metrics'),
                },
            }),
            this.fetchInternalConnectivity('metricsReadConfig'),
        ]).then(([metrics, connectivity]) => {
            const { hourly, daily } = { ...metrics }

            return {
                isPortalReachable: connectivity.endpoints.cybusPortal.reachable,
                hourly: (hourly || [])
                    .filter((h) => h.numberOfConnectedDatapoints !== 0 || h.numberOfReceivedMessages !== 0)
                    .map(({ numberOfConnectedDatapoints, numberOfReceivedMessages, timestamp }) => ({
                        numberOfConnectedDatapoints,
                        numberOfReceivedMessages,
                        timestamp: new Date(timestamp),
                    })),
                daily: (daily || []).map(({ numberOfConnectedDatapoints, numberOfReceivedMessages, date }) => ({
                    date: new Date(date),
                    numberOfConnectedDatapoints,
                    numberOfReceivedMessages,
                })),
            }
        })
    }

    sendMetricsToPortal (): Promise<void> {
        return this.request<'/api/system/metrics', 'PUT', void>({
            capability: Capability.SYSTEM_METRICS_MANAGE,
            method: 'PUT',
            path: '/api/system/metrics',
            authenticate: true,
            handlers: {
                200: () => Promise.resolve(),
                400: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'Unable to send metrics to portal'),
            },
        })
    }
}
